// Tiny client for the server's HTTP routes (separate from the WS).
import { getAccessToken } from '../hooks/useAuth.tsx';
import { track } from './analytics';

export const SERVER_URL = import.meta.env.DEV
  ? `http://localhost:${import.meta.env.VITE_SERVER_PORT ?? '3001'}`
  : '';

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const token = getAccessToken();
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...extra };
}

function authQuery(): string {
  const token = getAccessToken();
  return token ? `access_token=${encodeURIComponent(token)}` : '';
}

// ── Active bunker tracking ───────────────────────────────────────────────────
let _activeBunkerId: string | null = null;
export function setApiBunkerId(id: string | null): void {
  _activeBunkerId = id;
}

function bunkerQuery(): string {
  return _activeBunkerId ? `bunker=${encodeURIComponent(_activeBunkerId)}` : '';
}

/**
 * Joins a path with one or more query-string parts (non-empty strings only),
 * using `?` before the first part and `&` between subsequent parts.
 * Prevents double `??` and dangling `&`.
 */
function withQuery(path: string, ...parts: string[]): string {
  const nonEmpty = parts.filter(Boolean);
  if (nonEmpty.length === 0) return path;
  return `${path}?${nonEmpty.join('&')}`;
}

async function getJson<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(`${SERVER_URL}${path}`, { headers: authHeaders() });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

export interface FileEntry {
  name: string;
  relPath: string;
  size: number;
  mtime: string;
}

export async function getArtifacts(agentId: string): Promise<FileEntry[]> {
  const r = await getJson<{ ok: boolean; files: FileEntry[] }>(
    withQuery(`/artifacts/${agentId}`, bunkerQuery()),
  );
  return r?.files ?? [];
}

export async function getInbox(agentId: string): Promise<FileEntry[]> {
  const r = await getJson<{ ok: boolean; files: FileEntry[] }>(
    withQuery(`/inbox/${agentId}`, bunkerQuery()),
  );
  return r?.files ?? [];
}

export interface MemoryFile {
  name: string;
  size: number;
  mtime: string;
  content: string;
}

export async function getMemory(agentId: string): Promise<MemoryFile | null> {
  const r = await getJson<{ ok: boolean; memory: MemoryFile }>(
    withQuery(`/memory/${agentId}`, bunkerQuery()),
  );
  return r?.memory ?? null;
}

export async function saveMemory(agentId: string, content: string): Promise<boolean> {
  try {
    const res = await fetch(`${SERVER_URL}${withQuery(`/memory/${agentId}`, bunkerQuery())}`, {
      method: 'PUT',
      headers: authHeaders({ 'Content-Type': 'text/plain' }),
      body: content,
    });
    return res.ok;
  } catch {
    return false;
  }
}

// Encode each path segment individually so nested paths (e.g. "reports/file.pdf")
// are forwarded as real URL path segments, not as a single encoded segment.
function encodePath(relPath: string): string {
  return relPath.split('/').map(encodeURIComponent).join('/');
}

export function filePreviewUrl(agentId: string, scope: 'artifact' | 'inbox', name: string): string {
  return `${SERVER_URL}${withQuery(`/preview/${agentId}/${scope}/${encodePath(name)}`, authQuery(), bunkerQuery())}`;
}

export function fileDownloadUrl(agentId: string, scope: 'artifact' | 'inbox', name: string): string {
  return `${SERVER_URL}${withQuery(`/download/${agentId}/${scope}/${encodePath(name)}`, authQuery(), bunkerQuery())}`;
}

export interface ConversationSummary {
  filename: string;
  savedAt: string;
  messageCount: number;
  preview: string;
}

export async function getHistory(agentId: string): Promise<ConversationSummary[]> {
  const r = await getJson<{ ok: boolean; conversations: ConversationSummary[] }>(
    withQuery(`/history/${agentId}`, bunkerQuery()),
  );
  return r?.conversations ?? [];
}

export interface SavedConversation {
  savedAt: string;
  messages: { role: 'user' | 'assistant'; content: string }[];
  model?: string;
  mode?: string;
}

export async function loadConversationFile(
  agentId: string,
  filename: string,
): Promise<SavedConversation | null> {
  const r = await getJson<{ ok: boolean; conversation: SavedConversation }>(
    withQuery(`/history/${agentId}/${filename}`, bunkerQuery()),
  );
  return r?.conversation ?? null;
}

export async function getFileContent(
  agentId: string,
  scope: 'artifact' | 'inbox',
  name: string,
): Promise<string | null> {
  try {
    const res = await fetch(
      `${SERVER_URL}${withQuery(`/file/${agentId}/${scope}/${encodePath(name)}`, bunkerQuery())}`,
      { headers: authHeaders() },
    );
    if (!res.ok) return null;
    return await res.text();
  } catch {
    return null;
  }
}

export async function deleteArtifact(agentId: string, relPath: string): Promise<boolean> {
  try {
    const res = await fetch(
      `${SERVER_URL}${withQuery(`/artifacts/${agentId}/${encodePath(relPath)}`, bunkerQuery())}`,
      { method: 'DELETE', headers: authHeaders() },
    );
    return res.ok;
  } catch {
    return false;
  }
}

export async function deleteInboxFile(agentId: string, name: string): Promise<boolean> {
  try {
    const res = await fetch(
      `${SERVER_URL}${withQuery(`/inbox/${agentId}/${encodeURIComponent(name)}`, bunkerQuery())}`,
      { method: 'DELETE', headers: authHeaders() },
    );
    return res.ok;
  } catch {
    return false;
  }
}

export async function deleteConversation(agentId: string, filename: string): Promise<boolean> {
  try {
    const res = await fetch(
      `${SERVER_URL}${withQuery(`/history/${agentId}/${encodeURIComponent(filename)}`, bunkerQuery())}`,
      { method: 'DELETE', headers: authHeaders() },
    );
    return res.ok;
  } catch {
    return false;
  }
}

async function postAndRedirect(path: string, event?: string): Promise<void> {
  const res = await fetch(`${SERVER_URL}${path}`, { method: 'POST', headers: authHeaders() });
  if (!res.ok) throw new Error(`${path} failed: ${res.status}`);
  const { url } = (await res.json()) as { url: string };
  // Fired before the redirect: once the tab navigates to Stripe the beacon
  // would race the unload.
  if (event) track(event);
  window.location.href = url;
}

/** Start Stripe Checkout for the Founder plan (redirects the tab). */
export function startCheckout(): Promise<void> {
  return postAndRedirect('/billing/checkout', 'checkout_started');
}

/** Open the Stripe Customer Portal (redirects the tab). */
export function openBillingPortal(): Promise<void> {
  return postAndRedirect('/billing/portal');
}
